import Fuse from 'fuse.js';
import type { PlantingMergedResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import type { Field } from '/@/types/field';
import { AVAILABLE_CROPS } from './types';

export const FIELDVIEW_CROP_MAP: Record<string, string> = {
    corn: 'corn',
    soybeans: 'soy',
    wheat: 'wheat',
    barley: 'barley',
};

export const FIELDVIEW_RECENT_PLANTING_DAYS = 180;

export const SUPPORTED_FV_CROPS = ['Corn', 'Soybeans', 'Wheat'] as const;

type PlantingReportsList = { results: { mergedResults: PlantingMergedResult[] }[] } | undefined;

function mapFvCropToForm(cropName: string): (typeof AVAILABLE_CROPS)[number] | null {
    const m = FIELDVIEW_CROP_MAP[cropName.toLowerCase()];
    if (m && (AVAILABLE_CROPS as readonly string[]).includes(m)) {
        return m as (typeof AVAILABLE_CROPS)[number];
    }
    return null;
}

/**
 * Suggests "Cultivo antecesor" from (1) FieldView merged plantings, or (2) the crop on
 * the field monitoring with the newest planting date. Merged rows are sorted by planting
 * date (newest first). Pass `currentSourceReportId` as `plantingReport.report` for the
 * data prefilling this form: all rows with that `PlantingReportRequest` id are removed,
 * then the newest remaining row is the previous season from FieldView. If that id is
 * omitted, the next-newest row in the full list (`filtered[1]`) is used. If both an FV
 * candidate and a CM candidate exist, the one with the more recent planting date wins.
 */
export function getInferredPreviousCrop(
    field: Field | null | undefined,
    plantingQueryData: PlantingReportsList,
    currentSourceReportId: number | undefined
): (typeof AVAILABLE_CROPS)[number] | null {
    if (!field) return null;

    const lastCropWithHarvest = (field.cropMonitoring ?? [])
        .filter((cm) => !cm.active && !!cm.harvestDate)
        .sort((a, b) => new Date(b.harvestDate as string).getTime() - new Date(a.harvestDate as string).getTime())[0];

    const lastHarvestEnd = lastCropWithHarvest ? new Date(lastCropWithHarvest.harvestDate as string) : null;
    const days180Ago = new Date(Date.now() - FIELDVIEW_RECENT_PLANTING_DAYS * 24 * 60 * 60 * 1000);

    const merged: PlantingMergedResult[] = (plantingQueryData?.results || [])
        .flatMap((r) => r.mergedResults)
        .filter((r) => (SUPPORTED_FV_CROPS as readonly string[]).includes(r.cropName));

    const filtered = merged
        .filter((result) => {
            const date = new Date(result.plantingDate);
            return date >= days180Ago || (lastHarvestEnd && date > lastHarvestEnd);
        })
        .sort((a, b) => new Date(b.plantingDate).getTime() - new Date(a.plantingDate).getTime());

    const previousFvCandidate =
        currentSourceReportId != null ? filtered.filter((m) => m.report !== currentSourceReportId)[0] : filtered[1];
    const mappedFromFv = previousFvCandidate ? mapFvCropToForm(previousFvCandidate.cropName) : null;
    const fromPreviousReport =
        previousFvCandidate && mappedFromFv
            ? { crop: mappedFromFv, plantingDate: previousFvCandidate.plantingDate }
            : null;

    const withDates = (field.cropMonitoring ?? []).filter((cm) => cm.plantingDate);
    const sortedCM = [...withDates].sort(
        (a, b) => new Date(b.plantingDate).getTime() - new Date(a.plantingDate).getTime()
    );
    const newestCM = sortedCM[0];
    const isKnownCrop = (c: string): c is (typeof AVAILABLE_CROPS)[number] =>
        (AVAILABLE_CROPS as readonly string[]).includes(c);
    const fromNewestCM =
        newestCM?.crop && isKnownCrop(newestCM.crop)
            ? { crop: newestCM.crop, plantingDate: newestCM.plantingDate }
            : null;

    if (fromPreviousReport?.crop && fromNewestCM?.crop) {
        const tReport = new Date(fromPreviousReport.plantingDate).getTime();
        const tCM = new Date(fromNewestCM.plantingDate).getTime();
        return tCM >= tReport ? fromNewestCM.crop : fromPreviousReport.crop;
    }
    if (fromPreviousReport?.crop) return fromPreviousReport.crop;
    if (fromNewestCM?.crop) return fromNewestCM.crop;
    return null;
}

/**
 * Suggests environment from the past crop monitoring on the field for the same
 * crop, using the one with the newest planting date.
 */
export function getEnvironmentFromSameCropMonitoring(
    field: Field | null | undefined,
    crop: string | undefined
): number | undefined {
    if (!field || !crop) return undefined;
    const matches = (field.cropMonitoring ?? []).filter((cm) => cm.crop === crop && typeof cm.environment === 'number');
    if (!matches.length) return undefined;
    const best = [...matches].sort(
        (a, b) => new Date(b.plantingDate).getTime() - new Date(a.plantingDate).getTime()
    )[0];
    return best?.environment;
}

export function findBestProductMatch<T>(
    query: string,
    options: T[],
    getLabel: (o: T) => string,
    threshold = 0.6
): T | null {
    const indexed = options.map((item) => ({ item, label: getLabel(item) }));
    const fuse = new Fuse(indexed, {
        keys: ['label'],
        threshold,
        ignoreLocation: true,
        isCaseSensitive: false,
    });
    return fuse.search(query)[0]?.item.item ?? null;
}
